// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
} from '~/components/common';
import { FormPOJob } from '~/components/module/job';
// Others
import { IParamGetListPO, IPO, IPOProduct, IRequestPOList } from '~/utils/interface/po';
import { IBaseOption, IFilterValueChange, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { SearchByFilterContentEnum, SearchByFilterEnum, StatusJobEnum } from '~/utils/enum/common';
import useDebounce from '~/utils/hooks/useDebounce';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DASH,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import { formatCurrency, formatTitleCase, getFullName } from '~/utils/helper/common';
import { poActions, selectIsRefreshPOList } from '~/thunks/po/poSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { deletePO, getPOList, getVendorList } from '~/thunks/po/poThunk';
import { selectJobDetail } from '~/thunks/job/jobSlice';
// Styles, images, icons
import styles from './JobPO.module.scss';
import './JobPO.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

// Table columns
const columns = (t: TFunction): IHeaderCellType<IPOProduct>[] => [
  {
    id: 'name',
    label: t('table_po_title_name'),
    minWidth: 150,
    render: (record) => record.name,
  },
  {
    id: 'cost',
    label: t('table_po_title_cost'),
    minWidth: 50,
    render: (record) => formatCurrency(record.cost ?? DEFAULT_NUMBER_ZERO),
  },
  {
    id: 'quantity',
    label: t('table_po_title_quantity'),
    minWidth: 50,
    render: (record) => record.quantity,
  },
  {
    id: 'totalCost',
    label: t('table_po_title_total_cost'),
    render: (record) => formatCurrency(record.totalCost ?? DEFAULT_NUMBER_ZERO),
  },
];

const JobPO = () => {
  //#region Declare Hook
  const { t } = useTranslation(['admin/po']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const isRefreshPOList = useSelector(selectIsRefreshPOList);
  const jobDetail = useSelector(selectJobDetail);
  //#endregion Selector

  //#region Declare State
  const [poList, setPoList] = useState<IPO[]>([]);
  const [poSelected, setPoSelected] = useState<IPO>();
  const [vendorOption, setVendorOption] = useState<IBaseOption[]>([]);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [dataId, setDataId] = useState<string>(EMPTY_STRING);

  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [filterParams, setFilterParams] = useState<IParamGetListPO>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IParamGetListPO>({
    page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_1000,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowFormPO, setIsShowFormPO] = useState<boolean>(false);
  const [totalItemsPO, setTotalItemsPO] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamBase = {
      limit: DEFAULT_LIMIT_1000,
    };
    dispatch(getVendorList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;
        const option: IBaseOption[] = responses?.map((item) => {
          const data: IBaseOption = {
            label: item.name,
            value: item.id,
          };
          return data;
        });
        setVendorOption(option);
      })
      .catch(() => {})
      .finally(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!jobDetail.id) return;

    handleGetPOList(paramObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject, jobDetail.id]);

  useEffect(() => {
    if (!isRefreshPOList) return;

    handleGetPOList(paramObject);
    dispatch(poActions.setRefreshPOList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshPOList]);

  useEffect(() => {
    setParamObject({
      ...paramObject,
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields
        ?.map((item) => {
          return formatTitleCase(item);
        })
        ?.join(', ');

      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleApplyFilter = (values: IParamGetListPO | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values?.vendorIds) {
      setParamObject({
        ...paramObject,
        ...(values?.vendorIds ? { vendorIds: values?.vendorIds } : {}),
      });
    }
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryParamBase | undefined,
    onChange?: (value: IFilterValueChange<IQueryParamBase>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamBase = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }

    setFilterParams({ ...filterParams, fields: newFields?.fields });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleGetPOList = (params: IQueryParamBase) => {
    const request: IRequestPOList = {
      jobId: jobDetail.id,
      ...params,
    };
    setIsLoading(true);

    dispatch(getPOList(request))
      .unwrap()
      .then((res) => {
        const { responses, totalItems } = res?.data;

        if (!responses) return;

        setPoList(responses);
        setTotalItemsPO(totalItems.toString());
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setDataId(id);
    setIsOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);
    dispatch(deletePO(dataId))
      .unwrap()
      .then((res) => {
        setIsOpenConfirm(false);
        dispatch(poActions.setRefreshPOList(true));
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleShowFormPO = () => {
    setIsShowFormPO(!isShowFormPO);

    if (isShowFormPO) {
      setPoSelected(undefined);
    }
  };

  const handleEdit = (record: IPO) => {
    setPoSelected(record);
    setIsShowFormPO(true);
  };
  //#endRegion Handle Function

  return (
    <Box component='div' id='jobPOPage' className={cx('container')}>
      <Box className={cx('headerContainer')}>
        <BaseToolbar
          title={t('po_header_total_number', { total: totalItemsPO })}
          {...(jobDetail.status !== StatusJobEnum.COMPLETED && {
            textAction: t('button_add_po'),
            onAction: handleShowFormPO,
          })}
        >
          <Box className={cx('rightGroup')}>
            <BaseInputSearch
              searchByContent={
                searchPlaceholder
                  ? `${SearchByFilterContentEnum.PO_NUMBER}, ${searchPlaceholder}`
                  : SearchByFilterContentEnum.PO_NUMBER
              }
              value={searchKey}
              onChange={handleSearchChange}
            />

            <BaseFilters<IParamGetListPO>
              value={filterParams}
              defaultValue={FILTER_DEFAULT_VALUES}
              onApply={handleApplyFilter}
            >
              {({ value, onChange }) => (
                <Stack gap={'12px'}>
                  <Typography className={cx('title')}>
                    {t('common_action_search_by', { ns: 'common/base' })}
                  </Typography>

                  <FormGroup>
                    <FormControlLabel
                      className={cx('formControlLabel')}
                      slotProps={{
                        typography: {
                          className: cx('checkboxLabel'),
                        },
                      }}
                      control={
                        <Checkbox
                          name={SEARCH_BY_KEY}
                          className={cx('checkbox')}
                          value={SearchByFilterEnum.PRODUCT}
                          checked={value?.fields?.includes(SearchByFilterEnum.PRODUCT) || false}
                          onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                        />
                      }
                      label={t('filter_label_product_name')}
                    />

                    <FormControlLabel
                      className={cx('formControlLabel')}
                      slotProps={{
                        typography: {
                          className: cx('checkboxLabel'),
                        },
                      }}
                      control={
                        <Checkbox
                          name={SEARCH_BY_KEY}
                          className={cx('checkbox')}
                          value={SearchByFilterEnum.RECEIPT}
                          checked={value?.fields?.includes(SearchByFilterEnum.RECEIPT) || false}
                          onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                        />
                      }
                      label={t('filter_label_receipt')}
                    />
                  </FormGroup>

                  <Typography className={cx('title')}>
                    {t('common_action_filter_by', { ns: 'common/base' })}
                  </Typography>

                  <Box component={'div'} className={cx('filterGroup')}>
                    <InputLabel className={cx('label')}>{t('filter_label_vendor')}</InputLabel>

                    <BaseSelect
                      layout='horizontal'
                      width={'200px'}
                      multiple
                      options={vendorOption}
                      value={value?.vendorIds}
                      onChange={(value) => {
                        onChange &&
                          onChange({
                            name: 'vendorIds',
                            value: value as any,
                          });
                      }}
                    />
                  </Box>
                </Stack>
              )}
            </BaseFilters>
          </Box>
        </BaseToolbar>
      </Box>

      <Box className={cx('accordionWrap')}>
        {poList && poList.length > DEFAULT_NUMBER_ZERO ? (
          poList?.map((po, index) => (
            <Accordion
              key={po.id}
              className={cx('customAccordion')}
              sx={{
                boxShadow: 'none',
                '&:not(:last-of-type)': {
                  marginBottom: '0px',
                  marginTop: '0px',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: '64px',

                  '&.Mui-expanded': {
                    minHeight: '64px',
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                  },
                }}
              >
                <Box className={cx('summaryBox')}>
                  <Box component={'div'} className={cx('summaryTitle')}>
                    <Typography component='span' className={cx('text')}>
                      {po.poNumber}
                    </Typography>
                    <Typography component='span' className={cx('text')}>
                      {DASH}
                    </Typography>
                    <Typography component='span' className={cx('text', 'firstLastName')}>
                      {getFullName({
                        firstName: po.vendor?.name,
                      })}
                    </Typography>
                    {po?.receiptNumber && (
                      <Typography component='span' className={cx('text', 'firstLastName')}>
                        {`(${po?.receiptNumber})`}
                      </Typography>
                    )}
                  </Box>

                  <Stack component={'div'} direction='row' gap='50px' alignItems='center'>
                    <Typography component='span' className={cx('text')}>
                      {t('po_item_total', { cost: formatCurrency(po.total ?? DEFAULT_NUMBER_ZERO) })}
                    </Typography>

                    {jobDetail.status && jobDetail.status !== StatusJobEnum.COMPLETED && (
                      <Stack direction={'row'} alignItems={'center'}>
                        <IconButton
                          disabled={jobDetail.status === StatusJobEnum.COMPLETED}
                          sx={{
                            '&.Mui-disabled': {
                              cursor: 'not-allowed',
                              pointerEvents: 'unset',
                            },
                          }}
                          color='primary'
                          size='medium'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(po);
                          }}
                        >
                          <img
                            src={icons.tableIconActionEdit}
                            alt={t('common_alt_img')}
                            className={cx('icon')}
                          />
                        </IconButton>

                        <IconButton
                          disabled={jobDetail.status === StatusJobEnum.COMPLETED}
                          sx={{
                            '&.Mui-disabled': {
                              cursor: 'not-allowed',
                              pointerEvents: 'unset',
                            },
                          }}
                          size='medium'
                          color='error'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(po.id);
                          }}
                        >
                          <img
                            src={icons.activityIconDelete}
                            alt={t('common_alt_img')}
                            className={cx('icon')}
                          />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Box className={cx('body')}>
                  <BaseTable columns={columns(t)} dataSource={po.products ?? []} />
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Box component={'div'} className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Box>
        )}
      </Box>

      {isOpenConfirm && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isShowFormPO && <FormPOJob po={poSelected} onClose={handleShowFormPO} />}
    </Box>
  );
};

export default JobPO;
